import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useGetOrderDetail } from "@/lib/api/risk/brokerage/tt/v2";
import { TtOrderEvent, TtOrderMetadata, TtOrderSnapshot } from "@/lib/api/risk/brokerage/tt";
import { CollapsibleSection } from "./CollapsibleSection";
import { OrderContextHeader } from "./OrderContextHeader";
import { OrderEventsSection } from "./OrderEventsSection";
import { AssociatedMetadataSection } from "./AssociatedMetadataSection";
import { AttachOrderForm } from "../attach-form/AttachOrderForm";

interface OrderSidePanelProps {
  guidOrderId: string | null;
  wasResolved: boolean;
  onClose: () => void;
  /** When false, hide the AttachOrderForm section entirely (read-only panel). Default true. */
  showAttachForm?: boolean;
  /**
   * When true, render a read-only summary of the AssociatedMetaData (expected
   * types, OTC contract, crop year, should_ignore, customer splits) between
   * the Order Context and Order Events sections. Default false.
   *
   * Used by Active Orders where the form is hidden but the user still wants
   * to see what was previously associated.
   */
  showAssociatedMetadata?: boolean;
}

export function OrderSidePanel({
  guidOrderId,
  wasResolved,
  onClose,
  showAttachForm = true,
  showAssociatedMetadata = false,
}: OrderSidePanelProps) {
  // ESC closes the panel.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const detailResult = useGetOrderDetail(guidOrderId ?? "", { enabled: !!guidOrderId });
  const detail = detailResult.data?.data;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="text-sm font-semibold">Order Details</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <PanelBody
          guidOrderId={guidOrderId}
          wasResolved={wasResolved}
          isLoading={detailResult.isLoading}
          isError={detailResult.isError}
          error={detailResult.error}
          detail={detail}
          showAttachForm={showAttachForm}
          showAssociatedMetadata={showAssociatedMetadata}
        />
      </div>
    </div>
  );
}

interface PanelBodyProps {
  guidOrderId: string | null;
  wasResolved: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  detail:
    | {
        snapshot: TtOrderSnapshot | null;
        events: TtOrderEvent[];
        metadata: TtOrderMetadata | null;
      }
    | undefined;
  showAttachForm: boolean;
  showAssociatedMetadata: boolean;
}

function PanelBody({ guidOrderId, wasResolved, isLoading, isError, error, detail, showAttachForm, showAssociatedMetadata }: PanelBodyProps) {
  if (guidOrderId == null) {
    return (
      <p className="text-sm text-muted-foreground">
        Select an order from the table to enrich its data.
      </p>
    );
  }

  if (showAttachForm && wasResolved && !detail?.snapshot) {
    return (
      <p className="text-sm text-muted-foreground">
        ✓ Saved. Select another order from the table.
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading order details...</p>;
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600">
        Failed to load order details
        {error instanceof Error ? `: ${error.message}` : ""}
      </p>
    );
  }

  if (!detail || !detail.snapshot) {
    return <p className="text-sm text-muted-foreground">No snapshot found for this order.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <CollapsibleSection title="Order Context" defaultOpen={true}>
        <OrderContextHeader snapshot={detail.snapshot} />
      </CollapsibleSection>

      {showAssociatedMetadata && (
        <CollapsibleSection title="Associated Metadata" defaultOpen={true}>
          <AssociatedMetadataSection metadata={detail.metadata} />
        </CollapsibleSection>
      )}

      {showAttachForm && (
        <AttachOrderForm
          key={guidOrderId}
          guidOrderId={guidOrderId}
          snapshot={detail.snapshot}
          existingMetadata={detail.metadata}
        />
      )}

      <CollapsibleSection title={`Order Events (${detail.events.length})`} defaultOpen={false}>
        <OrderEventsSection
          events={detail.events}
          metadata={detail.metadata}
          guidOrderId={guidOrderId}
        />
      </CollapsibleSection>
    </div>
  );
}
